import { ActionMenu, Avatar, Badge, BanIcon, FlagIcon, MessageItem, TrashIcon } from 'armxlib'
import type { ActionMenuItem } from 'armxlib'
import { en } from '../../i18n/en'
import { formatTime } from '../../lib/format'
import type { Message } from './api'

const t = en.chat

/** What a reader can do on a message. */
export type MessageAction = 'report' | 'delete' | 'ban'

/** Props of {@link MessageRow}. */
export interface MessageRowProps {
  /** The message. */
  message: Message
  /** Id of the reader. */
  viewerId: string | undefined
  /** Whether the reader moderates. */
  moderator: boolean
  /** Called with the action picked in the menu. */
  onAction: (action: MessageAction, message: Message) => void
}

/**
 * One message, in plain text. Others' messages can be reported; moderators can
 * also delete any message and ban its author. Authors cannot delete their own.
 */
export function MessageRow({ message, viewerId, moderator, onAction }: MessageRowProps) {
  const author = message.author ?? null
  const own = author !== null && author.id === viewerId

  const items: ActionMenuItem[] = []
  if (!own) items.push({ label: t.report, icon: <FlagIcon />, onSelect: () => onAction('report', message) })
  if (moderator) {
    items.push({ label: t.delete, icon: <TrashIcon />, danger: true, onSelect: () => onAction('delete', message) })
    if (author && !own && !author.moderator) {
      items.push({ label: t.ban, icon: <BanIcon />, danger: true, onSelect: () => onAction('ban', message) })
    }
  }

  return (
    <MessageItem
      author={author?.username}
      avatar={<Avatar name={author?.username} />}
      deletedUser={author === null}
      deletedUserLabel={t.deletedUser}
      badge={author?.moderator ? <Badge>{t.modBadge}</Badge> : undefined}
      own={own}
      time={formatTime(message.created_at)}
      dateTime={message.created_at}
      actions={items.length > 0 ? <ActionMenu label={t.actions} items={items} /> : undefined}
    >
      {message.content}
    </MessageItem>
  )
}
